import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useMatches } from 'react-router';
import { z } from 'zod';

import { type SelectedEnvironment } from '../types';

// A route supplies a component here, so the handle check confirms the slot holds something callable.
const schema = z.object({
  EnvironmentFilters: z.custom<(props: Record<string, unknown>) => ReactNode>((val) => typeof val === 'function'),
});

/**
 * Render environment-specific filters for the current route.
 *
 * A route defines its filters via its `handle`.
 */
export function EnvironmentFilters({ environment }: { environment: SelectedEnvironment }) {
  const matches = useMatches();
  const currentRoute = matches.at(-1);

  const RouteFilters = useMemo(() => {
    if (currentRoute === undefined) {
      return null;
    }

    const parse = schema.safeParse(currentRoute.handle);
    if (!parse.success) {
      return null;
    }

    return parse.data.EnvironmentFilters;
  }, [currentRoute]);

  if (RouteFilters === null) {
    return null;
  }

  return <RouteFilters data-slot="environment-filter-trigger" environment={environment} />;
}
